import type { Edge } from '@xyflow/react';
import type { U3141ChildLotChain } from '../U3141Service';
import { parseConcordanceEntry, stripPartieSuffixFromLotSegment } from '../components/form/u3141ChildLotChain/U3141ConcordanceUtils';
import { u3141BuildGraphConcordanceEdge } from './u3141GraphConcordanceHandles';

export type U3141LotChainGraphEdgePair = { holderId: string; originId: string };

/** Construit les paires holder → origin (même règle que createInitialGraphFromGrid). */
export function u3141CollectLotChainGraphEdgePairs(data: U3141ChildLotChain[]): U3141LotChainGraphEdgePair[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  const idSet = new Set(data.map((row) => String(row.id)));
  const normalizeLot = (lot: string) => stripPartieSuffixFromLotSegment((lot ?? '').replace(/\s+/g, '').trim());
  const lotToRows = new Map<string, string[]>();
  data.forEach((row) => {
    const key = normalizeLot(row.lotDetail?.lot ?? '');
    if (!key) return;
    const arr = lotToRows.get(key) ?? [];
    arr.push(String(row.id));
    lotToRows.set(key, arr);
  });

  const pairs: U3141LotChainGraphEdgePair[] = [];
  const edgeSet = new Set<string>();

  const addPair = (holderId: string, originId: string) => {
    const key = `${holderId}:${originId}`;
    if (edgeSet.has(key)) return;
    edgeSet.add(key);
    pairs.push({ holderId, originId });
  };

  data.forEach((row) => {
    const childId = String(row.id);

    const treeParentId = row.parentChildLotChainId ? String(row.parentChildLotChainId) : null;
    if (treeParentId && idSet.has(treeParentId)) {
      addPair(treeParentId, childId);
    }

    const concordanceOrigines = row.lotDetail?.concordanceOrigines ?? [];
    concordanceOrigines.forEach((entry: string) => {
      const parsed = parseConcordanceEntry(entry);
      const refLotNorm = normalizeLot(parsed.lot ?? '');
      if (!refLotNorm) return;
      const candidates = lotToRows.get(refLotNorm) ?? [];
      if (candidates.length === 1 && candidates[0] !== childId) {
        addPair(candidates[0], childId);
      }
    });
  });

  return pairs;
}

/** Edges React Flow pour le graphe lotChainGraph. */
export function u3141BuildLotChainGraphEdges(data: U3141ChildLotChain[]): Edge[] {
  return u3141CollectLotChainGraphEdgePairs(data).map(({ holderId, originId }) =>
    u3141BuildGraphConcordanceEdge(holderId, originId),
  );
}
